import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeCustomAnswer,
  deliveryAnswerValue,
  replaceDeliveryAnswer,
  isBuiltinQuestion,
  isQuestionEnabled,
  CUSTOM_TEXT_ANSWER_LIMIT,
  type CustomAnswer,
  type CustomAnswerInfo,
  type CustomField,
} from '@lub0v/santa-utils';
import { Checkbox, Divider, Hint, Input, Radio, Stack, Typography } from '@src/components';
import { Textarea } from '@src/components';
import { WishText } from '@src/components/WishText/WishText';
import { santaClient } from '@src/state/client';
import './CustomQuestions.css';

export function useCustomQuestions(key: string, join = '', demoFields?: CustomField[]) {
  const [fields, setFields] = useState<CustomField[]>(demoFields ?? []);
  const [loadedKey, setLoadedKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const requestKey = JSON.stringify([key, join, attempt]);
  const loading = !demoFields && loadedKey !== requestKey;
  useEffect(() => {
    if (demoFields) return;
    let active = true;
    santaClient
      .getQuestions({ key, join })
      .then((res) => {
        if (!active) return;
        if (res.ok && res.data) {
          setFields(res.data);
          setError(null);
        } else setError(res.error?.message || 'newBox.saveFailed');
      })
      .catch(() => {
        if (active) setError('newBox.saveFailed');
      })
      .finally(() => {
        if (active) setLoadedKey(requestKey);
      });
    return () => {
      active = false;
    };
  }, [key, join, attempt, requestKey, demoFields]);
  return {
    fields: loading ? [] : fields,
    setFields,
    loading,
    error: loading ? null : error,
    retry: () => setAttempt((a) => a + 1),
  };
}
export function questionErrors(
  fields: CustomField[],
  answers: CustomAnswer[],
  previous?: CustomAnswerInfo[],
) {
  return Object.fromEntries(
    fields.filter(isQuestionEnabled).flatMap((field) => {
      const value = answers.find((a) => a.fieldId === field.id)?.value;
      const error = normalizeCustomAnswer(
        field,
        value,
        previous === undefined || previous.some((a) => a.fieldId === field.id),
      ).error;
      return error ? [[String(field.id), error]] : [];
    }),
  );
}
export function CustomQuestionsForm({
  fields,
  answers,
  onChange,
  errors = {},
  disabled = false,
  showHeading = true,
  wishHint,
  testIdPrefix,
  onFieldBlur,
}: {
  fields: CustomField[];
  answers: CustomAnswer[];
  onChange: (answers: CustomAnswer[]) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  showHeading?: boolean;
  wishHint?: string;
  testIdPrefix?: string;
  onFieldBlur?: () => void;
}) {
  const { t } = useTranslation();
  if (!fields.length) return null;
  const set = (fieldId: number, value: string | null) =>
    onChange([...answers.filter((a) => a.fieldId !== fieldId), { fieldId, value }]);
  return (
    <Stack gap={4}>
      {showHeading && (
        <>
          <Typography as="h2" role="title/sub">
            {t('questions.participantTitle')}
          </Typography>
          <Typography as="p" role="meta/helper">
            {t('questions.participantHint')}
          </Typography>
        </>
      )}
      {fields.filter(isQuestionEnabled).map((field, index) => {
        const id = field.id!;
        const value = answers.find((a) => a.fieldId === id)?.value ?? '';
        const errorKey = normalizeCustomAnswer(field, value, false).error || errors[String(id)];
        const c = field.config;
        const error = errorKey
          ? errorKey === 'questions.integerLimit'
            ? t(
                c.min !== undefined && c.max !== undefined
                  ? 'questions.integerRange'
                  : c.min !== undefined
                    ? 'questions.integerMinimum'
                    : 'questions.integerMaximum',
                { min: c.min, max: c.max },
              )
            : t(errorKey)
          : undefined;
        let selected: string[] = [];
        if (field.type === 'multiselect') {
          try {
            const stored = JSON.parse(value || '[]');
            // Drop removed/renamed options before composing the next edit, so an
            // invisible stale selection cannot make a valid new answer unsavable.
            selected = Array.isArray(stored)
              ? [
                  ...new Set(
                    stored.filter(
                      (option): option is string =>
                        typeof option === 'string' && (c.options ?? []).includes(option),
                    ),
                  ),
                ]
              : [];
          } catch {
            /* invalid old answer is editable */
          }
        }
        return (
          <Fragment key={id}>
            {index > 0 && <Divider />}
            <fieldset
              className="custom-question"
              disabled={disabled}
              onBlur={(event) => {
                // A choice/address group is one field; moving within it is still editing.
                if (!event.currentTarget.contains(event.relatedTarget)) onFieldBlur?.();
              }}
            >
              <legend>
                {index + 1}.{' '}
                {isBuiltinQuestion(field)
                  ? t(
                      `questions.types.${field.type === 'delivery_address' && field.config.deliveryMode === 'pickup' ? 'pickup_point' : field.type}`,
                    )
                  : field.title}{' '}
                {field.required ? (
                  <span className="custom-question__required">*</span>
                ) : (
                  `(${t('questions.optional')})`
                )}
              </legend>
              <Typography as="p" role="meta/helper">
                {!isBuiltinQuestion(field) && `${t('questions.fromOrganizer')} `}
                {field.type === 'santa_letter' && `${wishHint || t('questions.letterGuidance')} `}
                {t(
                  `questions.visibility.${field.visibleToSanta ? (field.visibleToOrganizer ? 'both' : 'santa') : field.visibleToOrganizer ? 'organizer' : 'owner'}`,
                )}
              </Typography>
              {field.type === 'santa_letter' && (
                <>
                  <Textarea
                    aria-label={t('questions.types.santa_letter')}
                    testId={testIdPrefix ? `${testIdPrefix}-wish` : undefined}
                    value={value}
                    onValueChange={(next) => set(id, next)}
                    maxLength={32768}
                    rows={5}
                    error={error}
                  />
                </>
              )}
              {(['phone', 'pickup_point'].includes(field.type) ||
                (field.type === 'delivery_address' && field.config.deliveryMode === 'pickup')) && (
                <Input
                  className={field.type === 'phone' ? 'custom-question__phone' : undefined}
                  aria-label={t(
                    `questions.types.${field.type === 'delivery_address' && field.config.deliveryMode === 'pickup' ? 'pickup_point' : field.type}`,
                  )}
                  value={
                    field.type === 'delivery_address' ? deliveryAnswerValue(field, value) : value
                  }
                  type={field.type === 'phone' ? 'tel' : 'text'}
                  name={field.type === 'phone' ? 'phone' : 'pickupPoint'}
                  maxLength={field.type === 'phone' ? 32 : 1000}
                  onValueChange={(next) =>
                    set(
                      id,
                      field.type === 'delivery_address'
                        ? replaceDeliveryAnswer(field, value, next)
                        : next,
                    )
                  }
                  error={error}
                />
              )}
              {(field.type === 'postal_address' ||
                (field.type === 'delivery_address' && field.config.deliveryMode !== 'pickup')) &&
                (() => {
                  let address = { recipient: '', address: '', code: '' };
                  try {
                    address = {
                      ...address,
                      ...JSON.parse(
                        (field.type === 'delivery_address'
                          ? deliveryAnswerValue(field, value)
                          : value) || '{}',
                      ),
                    };
                  } catch {
                    /* Invalid stored answers remain editable. */
                  }
                  if (address.code) {
                    address.address = [address.address, address.code].filter(Boolean).join(', ');
                    address.code = '';
                  }
                  return (
                    <>
                      {(['recipient', 'address'] as const).map((key) => (
                        <Input
                          key={key}
                          name={key === 'recipient' ? 'postalRecipient' : 'postalAddress'}
                          label={t(`game.cardCreate.delivery.${key}`)}
                          value={address[key]}
                          maxLength={256}
                          hint={key === 'address' ? t('questions.fullAddressHint') : undefined}
                          autoComplete={
                            key === 'recipient' ? 'shipping name' : 'shipping street-address'
                          }
                          onValueChange={(next) =>
                            set(
                              id,
                              field.type === 'delivery_address'
                                ? replaceDeliveryAnswer(
                                    field,
                                    value,
                                    JSON.stringify({ ...address, [key]: next }),
                                  )
                                : JSON.stringify({ ...address, [key]: next }),
                            )
                          }
                        />
                      ))}
                      {error && <Hint>{error}</Hint>}
                    </>
                  );
                })()}
              {['string', 'integer'].includes(field.type) && (
                <Input
                  className={field.type === 'integer' ? 'custom-question__number' : undefined}
                  aria-label={field.title}
                  value={
                    field.type === 'delivery_address' ? deliveryAnswerValue(field, value) : value
                  }
                  type={field.type === 'integer' ? 'number' : 'text'}
                  step={field.type === 'integer' ? 1 : undefined}
                  min={c.min}
                  max={c.max}
                  maxLength={
                    field.type === 'string'
                      ? Math.min(c.maxLength ?? CUSTOM_TEXT_ANSWER_LIMIT, CUSTOM_TEXT_ANSWER_LIMIT)
                      : undefined
                  }
                  placeholder={c.placeholder}
                  onValueChange={(v) => set(id, v)}
                  error={error}
                />
              )}
              {field.type === 'boolean' && (
                <Stack gap={2}>
                  {['true', 'false'].map((v) => (
                    <Radio
                      key={v}
                      name={`question-${id}`}
                      checked={value === v}
                      onCheckedChange={() => set(id, v)}
                      label={t(v === 'true' ? 'questions.yes' : 'questions.no')}
                    />
                  ))}
                  {!field.required && (
                    <Radio
                      name={`question-${id}`}
                      checked={value === ''}
                      onCheckedChange={() => set(id, null)}
                      label={t('questions.noAnswer')}
                    />
                  )}
                </Stack>
              )}
              {field.type === 'select' && (
                <Stack gap={2}>
                  {(c.options ?? []).map((option) => (
                    <Radio
                      key={option}
                      name={`question-${id}`}
                      checked={value === option}
                      onCheckedChange={() => set(id, option)}
                      label={option}
                    />
                  ))}
                  {!field.required && (
                    <Radio
                      name={`question-${id}`}
                      checked={value === ''}
                      onCheckedChange={() => set(id, null)}
                      label={t('questions.noAnswer')}
                    />
                  )}
                </Stack>
              )}
              {field.type === 'multiselect' &&
                (c.options ?? []).map((v) => (
                  <Checkbox
                    key={v}
                    checked={selected.includes(v)}
                    label={v}
                    onCheckedChange={(checked) =>
                      set(
                        id,
                        JSON.stringify(
                          checked ? [...selected, v] : selected.filter((o) => o !== v),
                        ),
                      )
                    }
                  />
                ))}
              {['boolean', 'select', 'multiselect'].includes(field.type) && error && (
                <Hint>{error}</Hint>
              )}
            </fieldset>
          </Fragment>
        );
      })}
    </Stack>
  );
}
export function CustomAnswers({
  answers,
  showEmpty = false,
  startNumber = 1,
}: {
  answers?: CustomAnswerInfo[];
  showEmpty?: boolean;
  startNumber?: number;
}) {
  const { t } = useTranslation();
  const filled = (answers ?? []).filter(
    (a) =>
      isQuestionEnabled(a.field) &&
      (showEmpty || (a.value !== null && a.value !== '' && a.value !== '[]')),
  );
  if (!filled.length) return null;
  return (
    <div className="custom-answers">
      {filled.map((a, index) => {
        let text = a.value ?? '';
        if (a.field.type === 'delivery_address') text = deliveryAnswerValue(a.field, text);
        if (
          a.field.type === 'postal_address' ||
          (a.field.type === 'delivery_address' && a.field.config.deliveryMode !== 'pickup')
        ) {
          try {
            const address = JSON.parse(text);
            text = [address.recipient, address.address, address.code].filter(Boolean).join('\n');
          } catch {
            /* Display legacy text safely. */
          }
        }
        if (a.field.type === 'boolean' && text)
          text = t(text === 'true' ? 'questions.yes' : 'questions.no');
        if (a.field.type === 'multiselect') {
          try {
            text = JSON.parse(text).join(', ');
          } catch {
            /* display legacy text safely */
          }
        }
        if (!text.trim() || text === '[]') text = t('box.organizer.preview.empty');
        return (
          <section className="card-sheet__section" key={a.fieldId}>
            <Typography as="h3" role="title/card">
              {startNumber + index}.{' '}
              {isBuiltinQuestion(a.field)
                ? t(
                    `questions.types.${a.field.type === 'delivery_address' && a.field.config.deliveryMode === 'pickup' ? 'pickup_point' : a.field.type}`,
                  )
                : a.field.title}
            </Typography>
            <div className="card-sheet__body custom-answers__value">
              {a.field.type === 'santa_letter' ? <WishText>{text}</WishText> : text}
            </div>
          </section>
        );
      })}
    </div>
  );
}
